import { type FC, useEffect, useMemo, useState } from "react"
import { useParams } from "react-router"
import styled from "styled-components"

import { type Downloadable, type DownloadableTag } from "@forento/core/models/downloadable"
import { getExtensionByFileName, getFileNameFromPath } from "@forento/core/utilities/file"
import { parseNumber } from "@forento/core/utilities/number"
import Tooltip from "@forento/ui/components/Tooltip"
import { useFile } from "@forento/ui/hooks/useFile"
import { toggleStateArray } from "@forento/ui/utilities/array"
import { cn } from "@forento/ui/utilities/tailwind"

import FileChooser from "~/components/FileChooser"
import { CheckmarkIcon, CloseIcon, SettingsIcon } from "~/components/Icon"
import ImageUploader from "~/components/ImageUploader"
import InputField, { useRichTextArea } from "~/components/InputField"
import InputLabel from "~/components/InputLabel"
import Layout, { PageBreadcrumb, PageHeader } from "~/components/Layout"
import ManageTagsModal from "~/components/ManageTagsModal"
import PartialLoadingPage from "~/components/PartialLoadingPage"
import ProductPricing from "~/components/ProductPricing"
import RichContentEditor from "~/components/RichContentEditor"
import ZapierId from "~/components/ZapierId"
import { useAlert } from "~/contexts/AlertContext"
import { useToast } from "~/contexts/ToastContext"
import useExternalBuyLink from "~/hooks/useExternalBuyLink"
import useFileUpload from "~/hooks/useFileUpload"
import usePrice from "~/hooks/usePrice"
import {
	exportEditedRichContent,
	loadRichContent,
	parseRichContent,
	type RichContentValue,
} from "~/utilities/richContent"
import routes from "~/utilities/routes"
import trpc, { query } from "~/utilities/trpc"

const EditDownloadablePage: FC = () => {
	const downloadableId = parseNumber(useParams().downloadableId) ?? -1

	const [thumbnail, setThumbnail] = useState<File | null>()

	const downloadable = query.downloadable.get.useQuery(downloadableId)
	const tags = query.downloadable.listTags.useQuery()

	useEffect(() => {
		if (!downloadable.data) return

		if (downloadable.data.thumbnailUrl === null) {
			setThumbnail(null)
			return
		}

		const abortController = new AbortController()
		fetch(downloadable.data.thumbnailUrl, { signal: abortController.signal })
			.then(async x => ({
				name: getFileNameFromPath(x.url),
				blob: await x.blob(),
			}))
			.then(({ name, blob }) => {
				if (abortController.signal.aborted) return
				setThumbnail(new File([blob], name ?? "Thumbnail", { type: blob.type }))
			})

		return () => {
			abortController.abort()
		}
	}, [downloadable.data])

	return (
		<Layout>
			<PageHeader>
				<PageBreadcrumb
					path={[{ title: "Downloadables", link: routes.downloadable.index() }]}
					title={downloadable.data?.name ?? "..."}
				/>
				{downloadable.data && <ZapierId id={downloadable.data.id} />}
			</PageHeader>
			{downloadable.error || tags.error ? (
				<p>Failed to fetch downloadable.</p>
			) : !downloadable.data || tags.data === undefined || thumbnail === undefined ? (
				<PartialLoadingPage />
			) : (
				<Content
					downloadable={{ ...downloadable.data, thumbnail }}
					tags={tags.data}
					reload={downloadable.refetch}
					reloadTags={tags.refetch}
				/>
			)}
		</Layout>
	)
}

type ContentProps = {
	downloadable: Downloadable & { thumbnail: File | null }
	tags: DownloadableTag[]
	reload(): Promise<unknown>
	reloadTags(): void
}
const Content: FC<ContentProps> = ({ downloadable, tags, reload, reloadTags }) => {
	const toast = useToast()
	const alert = useAlert()

	const [name, setName] = useState(downloadable.name)
	const [shortDescription, setShortDescription] = useState(downloadable.shortDescription ?? "")
	const longDescription = useRichTextArea({ label: "Description", initialValue: downloadable.longDescription })
	const [presentation, setPresentation] = useState<RichContentValue | null>(
		loadRichContent(downloadable.presentation),
	)
	const [isSignInRequired, setSignInRequired] = useState(downloadable.isSignInRequired)
	const { price, priceValue, setPrice } = usePrice(downloadable.price?.amount)
	const [isManualOnly, setManualOnly] = useState(downloadable.isManualOnly)
	const externalBuyLink = useExternalBuyLink(downloadable.externalBuyLink)
	const [isBokaderoMemberRequired, setBokaderoMemberRequired] = useState(
		downloadable.isBokaderoMemberRequired ?? false,
	)
	const [membershipIds, setMembershipIds] = useState<number[]>(downloadable.membershipIds)
	const [isPaused, setPaused] = useState(downloadable.pauseDate !== null)
	const [selectedTags, setSelectedTags] = useState<number[]>(downloadable.tags.map(x => x.id))
	const [newDownloadableFile, setNewDownloadableFile] = useState<File | null>(null)

	const [isManageTagsModalOpen, setManageTagsModalOpen] = useState(false)

	const thumbnail = useFile(downloadable.id, downloadable.thumbnailUrl)

	const fileUpload = useFileUpload()

	useEffect(() => {
		setName(downloadable.name)
		setShortDescription(downloadable.shortDescription ?? "")
		setPresentation(loadRichContent(downloadable.presentation))
		setSignInRequired(downloadable.isSignInRequired ?? false)
		setPrice(downloadable.price?.amount.toString() ?? "")
		setManualOnly(downloadable.isManualOnly)
		externalBuyLink.reset(downloadable.externalBuyLink)
		setBokaderoMemberRequired(downloadable.isBokaderoMemberRequired ?? false)
		setMembershipIds(downloadable.membershipIds)
		setPaused(downloadable.pauseDate !== null)
		setSelectedTags(downloadable.tags.map(x => x.id))
		setNewDownloadableFile(null)
	}, [downloadable, externalBuyLink.reset, setPrice])

	useEffect(() => {
		const isModified =
			name.trim() !== downloadable.name.trim() ||
			shortDescription.trim() !== (downloadable.shortDescription ?? "").trim() ||
			longDescription.isModified ||
			presentation?.isModified ||
			isSignInRequired !== downloadable.isSignInRequired ||
			priceValue !== (downloadable.price?.amount ?? null) ||
			isManualOnly !== downloadable.isManualOnly ||
			externalBuyLink.isModified ||
			isBokaderoMemberRequired !== (downloadable.isBokaderoMemberRequired ?? false) ||
			membershipIds.toSorted().join(",") !== downloadable.membershipIds.toSorted().join(",") ||
			isPaused !== (downloadable.pauseDate !== null) ||
			selectedTags.toSorted().join(",") !==
				downloadable.tags
					.map(x => x.id)
					.toSorted()
					.join(",") ||
			thumbnail.isModified ||
			newDownloadableFile !== null

		if (isModified) {
			toast.setUnsavedChanges(async () => {
				if (name.trim().length === 0) return false

				if (isManualOnly && externalBuyLink.error !== null) {
					await alert.show("Error", externalBuyLink.error)
					return false
				}

				try {
					const uploaded =
						newDownloadableFile !== null
							? await fileUpload.upload({
									file: newDownloadableFile,
									kind: "downloadable-file",
									fileExtension: getExtensionByFileName(newDownloadableFile.name) ?? undefined,
								})
							: undefined
					if (uploaded === null) return false
					await trpc.downloadable.update.mutate({
						id: downloadable.id,
						data: {
							name,
							shortDescription,
							longDescription: await longDescription.exportEditorState(),
							presentation: exportEditedRichContent(presentation),
							isSignInRequired,
							price: priceValue,
							isManualOnly,
							externalBuyLink: isManualOnly ? externalBuyLink.value : null,
							isBokaderoMemberRequired,
							membershipIds,
							isPaused,
							thumbnailDataUrl: thumbnail.isModified ? await thumbnail.exportToDataUrl() : undefined,
							tagIds: selectedTags,
							fileName: uploaded?.fileName,
						},
					})
					await reload()
					return true
				} catch (error) {
					console.error(error)
					await alert.show("Error", "Failed to save downloadable. Please try again.")
					return false
				}
			})
		} else {
			toast.clearUnsavedChanges()
		}
	}, [
		alert,
		downloadable,
		isSignInRequired,
		longDescription,
		presentation,
		isManualOnly,
		externalBuyLink,
		isBokaderoMemberRequired,
		membershipIds,
		name,
		priceValue,
		isPaused,
		selectedTags,
		reload,
		shortDescription,
		thumbnail,
		toast,
		newDownloadableFile,
	])

	const initialPresentation = useMemo(() => parseRichContent(downloadable.presentation), [downloadable.presentation])

	return (
		<Container>
			<ManageTagsModal
				type="downloadable"
				isOpen={isManageTagsModalOpen}
				onClose={() => setManageTagsModalOpen(false)}
				onChange={reloadTags}
				tags={tags}
			/>

			<Section>
				{downloadable.type === "file" ? (
					<div>
						<InputLabel>Replace file</InputLabel>
						<FileChooser type="file" value={newDownloadableFile} onChange={setNewDownloadableFile} />
					</div>
				) : (
					<div>
						<InputLabel>Link</InputLabel>
						<p className="text-light text-sm">{downloadable.url}</p>
					</div>
				)}
				<InputField label="Name" value={name} onChange={setName} />
				<InputField label="Short description" value={shortDescription} onChange={setShortDescription} />
				{longDescription.element}
				<ImageUploader label="Thumbnail" recommendedSize="thumbnail" {...thumbnail.imageUploader} />
			</Section>

			<Section>
				<Title>Pricing</Title>
				<ProductPricing
					type="downloadable"
					isSignInRequired={isSignInRequired}
					setSignInRequired={setSignInRequired}
					price={price}
					setPrice={setPrice}
					isManualOnly={isManualOnly}
					externalBuyLink={externalBuyLink}
					setManualOnly={setManualOnly}
					isBokaderoMemberRequired={isBokaderoMemberRequired}
					setBokaderoMemberRequired={setBokaderoMemberRequired}
					membershipIds={membershipIds}
					setMembershipIds={setMembershipIds}
					isPaused={isPaused}
					setPaused={setPaused}
				/>
			</Section>

			<Section>
				<div className="flex items-center gap-2">
					<Title>Tags</Title>
					<Tooltip tooltip="Manage tags">
						<button
							type="button"
							className="block cursor-pointer"
							onClick={() => setManageTagsModalOpen(true)}
						>
							<SettingsIcon className="h-6 w-6" />
						</button>
					</Tooltip>
				</div>
				<div className="flex flex-wrap gap-2">
					{tags.map(tag => {
						const isSelected = selectedTags.includes(tag.id)
						const Icon = isSelected ? CheckmarkIcon : CloseIcon
						return (
							<button
								key={tag.id}
								type="button"
								className={cn(
									"flex cursor-pointer items-center gap-2 rounded-4xl px-4 py-2 text-white",
									isSelected ? "bg-primary" : "bg-light",
								)}
								onClick={() => toggleStateArray(setSelectedTags, tag.id)}
							>
								<Icon className="size-6" />
								{tag.label}
							</button>
						)
					})}
				</div>
			</Section>

			<Section>
				<Title>Presentation</Title>
				<RichContentEditor
					placeholder="Enter downloadable presentation here..."
					initialValue={initialPresentation}
					setValue={setPresentation}
				/>
			</Section>
		</Container>
	)
}

const Container = styled.div`
	display: flex;
	flex-direction: column;
	gap: 16px;
	padding-bottom: 128px;
`

const Section = styled.div`
	display: flex;
	flex-direction: column;
	gap: 16px;
`

const Title = styled.h2`
	font-weight: 600;
	font-size: 24px;
	color: black;
`

export default EditDownloadablePage
